import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Chip,
  DataTable,
  Icon,
  List,
  ListItem,
  Menu,
  Notice,
  Skeleton,
  StatTile,
  iconDownload,
  useSnackbar,
} from '@sauda/ui/kit';
import { Screen } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { downloadFile } from '../lib/download.js';
import { OwingList } from './OwingList.js';
import { formatRupees } from '../lib/money.js';
import { formatCalendarDate, formatMonth } from '../lib/time.js';

interface PassbookLine {
  id: string;
  branchName: string;
  propertyTitle: string;
  expectedPaise: number;
  receivedPaise: number;
  duePaise: number;
  dueOn: string | null;
  overdue: boolean;
}
interface Summary {
  expectedPaise: number;
  receivedPaise: number;
  duePaise: number;
  overduePaise: number;
  incentiveOwedPaise: number;
  deals: number;
}
interface Group extends Summary {
  branchId?: string;
  memberId?: string;
  branchName?: string;
  name?: string;
}
interface Dashboard {
  totals: Summary;
  branches: Group[];
  agents: Group[];
  months: { month: string; expectedPaise: number; receivedPaise: number; duePaise: number }[];
}

const KINDS = ['deals', 'receipts', 'renewals', 'introductions'] as const;

/** The passbook: totals, the branch and agent comparison, months, and a row for every deal. */
export function PassbookScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const snack = useSnackbar();
  const [branch, setBranch] = useState('');
  const filter = branch === '' ? '' : `?branchId=${branch}`;
  const lines = useQuery({
    queryKey: ['passbook', auth.agencyId, branch],
    queryFn: () => api<PassbookLine[]>(`/passbook${filter}`),
  });
  const dashboard = useQuery({
    queryKey: ['passbook-summary', auth.agencyId, branch],
    queryFn: () => api<Dashboard>(`/passbook/dashboard${filter}`),
  });
  const allBranches = useQuery({
    queryKey: ['passbook-branches', auth.agencyId],
    queryFn: () => api<Dashboard>('/passbook/dashboard'),
    enabled: auth.current?.role === 'owner' || auth.current?.role === 'accounts',
  });
  const download = async (kind: string) => {
    try {
      await downloadFile(`/export/${kind}.csv`, `${kind}.csv`);
      snack({ text: t('passbook.exported', { file: `${kind}.csv` }) });
    } catch {
      snack({ text: t('passbook.exportFailed') });
    }
  };
  const d = dashboard.data;
  const line = (g: Group) =>
    t('passbook.groupLine', {
      received: formatRupees(g.receivedPaise),
      expected: formatRupees(g.expectedPaise),
      due: formatRupees(g.duePaise),
      overdue: formatRupees(g.overduePaise),
    });
  return (
    <Screen title={t('passbook.title')} large wide eyebrow={auth.current?.agencyName}>
      <div className="chips-row">
        <Menu
          label={t('passbook.exports')}
          items={KINDS.map((kind) => ({
            key: kind,
            label: t(`passbook.export_${kind}`),
            icon: iconDownload,
            onSelect: () => void download(kind),
          }))}
          trigger={(props) => (
            <Button {...props} variant="tonal">
              <Icon icon={iconDownload} size={20} />
              {t('passbook.exports')}
            </Button>
          )}
        />
      </div>
      {allBranches.data && allBranches.data.branches.length > 1 ? (
        <div className="chips-row" role="group" aria-label={t('passbook.filterBranch')}>
          <Chip selected={branch === ''} onClick={() => setBranch('')}>
            {t('passbook.all')}
          </Chip>
          {allBranches.data.branches.map((b) => (
            <Chip
              key={b.branchId}
              selected={branch === b.branchId}
              onClick={() => setBranch(b.branchId ?? '')}
            >
              {b.branchName}
            </Chip>
          ))}
        </div>
      ) : null}
      {dashboard.isPending ? <Skeleton height={120} /> : null}
      {dashboard.isError || lines.isError ? (
        <Notice tone="error">{t('passbook.loadFailed')}</Notice>
      ) : null}
      {d === undefined ? null : (
        <>
          <h2 className="k-t-title-sm">{t('passbook.summary')}</h2>
          <div className="stat-grid">
            <div data-testid="passbook-expected">
              <StatTile
                label={t('passbook.expected')}
                value={formatRupees(d.totals.expectedPaise)}
              />
            </div>
            <div data-testid="passbook-received">
              <StatTile
                label={t('passbook.received')}
                value={formatRupees(d.totals.receivedPaise)}
              />
            </div>
            <StatTile label={t('passbook.due')} value={formatRupees(d.totals.duePaise)} />
            <StatTile
              label={t('passbook.overdue')}
              value={formatRupees(d.totals.overduePaise)}
              tone={d.totals.overduePaise > 0 ? 'error' : 'default'}
            />
            <StatTile
              label={t('passbook.incentiveOwed')}
              value={formatRupees(d.totals.incentiveOwedPaise)}
            />
          </div>
          <section className="today-section">
            <h2 className="k-t-title-sm">{t('owing.title')}</h2>
            <OwingList />
          </section>
          {d.branches.length > 1 ? (
            <section className="today-section">
              <h2 className="k-t-title-sm">{t('passbook.branchComparison')}</h2>
              <List label={t('passbook.branchComparison')}>
                {d.branches.map((b) => (
                  <ListItem key={b.branchId} title={b.branchName ?? ''} supporting={line(b)} />
                ))}
              </List>
            </section>
          ) : null}
          {d.agents.length > 0 && auth.current?.role !== 'agent' ? (
            <section className="today-section">
              <h2 className="k-t-title-sm">{t('passbook.agents')}</h2>
              <List label={t('passbook.agents')}>
                {d.agents.map((a) => (
                  <ListItem key={a.memberId} title={a.name ?? ''} supporting={line(a)} />
                ))}
              </List>
            </section>
          ) : null}
          {d.months.length > 0 ? (
            <Card>
              <h2 className="k-t-title-sm">{t('passbook.byMonth')}</h2>
              {d.months.map((m) => (
                <p key={m.month} className="k-t-body">
                  <span className="k-t-label">
                    {m.month === 'none' ? t('passbook.noDue') : formatMonth(m.month)}
                  </span>{' '}
                  {m.month === 'none'
                    ? t('passbook.noDueLine', { due: formatRupees(m.duePaise) })
                    : m.expectedPaise === 0
                      ? t('passbook.monthReceivedOnly', { received: formatRupees(m.receivedPaise) })
                      : t('passbook.monthDueLine', {
                          received: formatRupees(m.receivedPaise),
                          expected: formatRupees(m.expectedPaise),
                          due: formatRupees(m.duePaise),
                        })}
                </p>
              ))}
            </Card>
          ) : null}
        </>
      )}
      <section className="today-section">
        <h2 className="k-t-title-sm">{t('passbook.deals')}</h2>
        <DataTable
          label={t('passbook.deals')}
          rows={lines.data ?? []}
          rowKey={(l) => l.id}
          empty={
            lines.isSuccess ? <p className="k-t-body k-muted">{t('passbook.noDeals')}</p> : null
          }
          columns={[
            { key: 'property', header: t('passbook.property'), render: (l) => l.propertyTitle },
            { key: 'branch', header: t('passbook.branch'), render: (l) => l.branchName },
            {
              key: 'due',
              header: t('passbook.dueOn'),
              render: (l) => (
                <>
                  {l.dueOn === null ? t('passbook.noDueDate') : formatCalendarDate(l.dueOn)}
                  {l.overdue ? ` · ${t('passbook.overdue')}` : ''}
                </>
              ),
            },
            {
              key: 'money',
              header: t('passbook.receivedOfExpected'),
              align: 'end',
              render: (l) => `${formatRupees(l.receivedPaise)} / ${formatRupees(l.expectedPaise)}`,
            },
          ]}
        />
      </section>
    </Screen>
  );
}
